import React from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

import { showModal } from '../../../actions/modal';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  .stock-wrapper {
    margin-top: 12px;
    margin-bottom: 40px;
    .stock-row {
      display: flex;
      align-items: center;
      color: #72747b;
      margin-bottom: 14px;
      &:last-child {
        margin-bottom: 0;
      }
      .stock-expired {
        margin-left: auto;
        margin-top: 3px;
        font-size: 11px;
        color: red;
      }
      .stock-defective {
        margin-left: 20px;
        margin-top: 3px;
        font-size: 11px;
        color: red;
      }
      .out-of-stock {
        margin-left: 20px;
        margin-top: 3px;
        font-size: 11px;
        color: #72747b;
      }
      .stock-reserved {
        margin-left: 20px;
        margin-top: 3px;
        font-size: 11px;
        color: #aaaaaa;
        &.stock-reserved-hover {
          cursor: pointer;
          &:hover {
            text-decoration: underline;
          }
        }
      }
      .stock-total {
        margin-left: 15px;
        color: #7e57c2;
        cursor: pointer;
        min-width: 50px;
        text-align: right;

        &--disabled {
          color: #343a40;
          cursor: unset;
        }
      }
    }
  }
`;

const StockBlock = (props) => {
  const { stock, variant, warehouses, handleChangeInput, handleSubmit, product } = props;
  const dispatch = useDispatch();

  const { t } = useAutoTranslation();

  const skus = useSelector((state) => state.items.statisticsOfUsingChestnyznak);

  const actualWarehouses = stock.reduce((a, c) => {
    const findWarehouse = warehouses.find((w) => w.code === c.warehouseCode);
    if (findWarehouse) {
      if (
        findWarehouse.goodsCondition !== 'DEFECTIVE' &&
        findWarehouse.goodsCondition !== 'EXPIRED'
      ) {
        a.push({
          ...c,
          warehouseTitle: findWarehouse.title,
          operatorType: findWarehouse.operatorType,
        });
      }
    }
    return a;
  }, []);

  const handleStockTotalModal = (stockData) => {
    dispatch(
      showModal('EditStockTotal', {
        stockData,
        submitCallback: async (code, newTotalValue) => {
          handleChangeInput(
            'stock',
            stock.map((s) => ({
              warehouseCode: s.warehouseCode,
              stockTotal: s.warehouseCode === code ? newTotalValue : s.stockTotal,
            })),
          );
          handleChangeInput('updateStockTotal', true);
          return await handleSubmit();
        },
      }),
    );
  };

  const getStocksWithTitles = (variantStocks, warehouses) => {
    for (let i = 0; i < variantStocks.length; i++) {
      for (let j = 0; j < warehouses.length; j++) {
        if (variantStocks[i].warehouseCode === warehouses[j].code) {
          variantStocks[i]['title'] = warehouses[j].title;
        }
      }
    }

    return variantStocks;
  };

  const [stockWithDeficit, setStockWithDeficit] = useState([]);

  useEffect(() => {
    if (warehouses && product && skus) {
      const currentSku = skus.find((sku) => sku.sku_id === variant.id);

      if (currentSku) {
        const stocksWithTitle = getStocksWithTitles(variant.stock, warehouses);

        for (let i = 0; i < stocksWithTitle.length; i++) {
          if (stocksWithTitle[i].stockAvailable > currentSku.serial_numbers[0].free) {
            const difference =
              stocksWithTitle[i].stockAvailable - currentSku.serial_numbers[0].free;
            stocksWithTitle[i]['difference'] = difference;
          }
        }

        const stockWithDeficit = stocksWithTitle.filter((s) => s.difference > 0);
        setStockWithDeficit(stockWithDeficit);
      }
    }
  }, [warehouses, product, skus]);

  const newWarehouses = actualWarehouses.filter(
    (item) => !item.warehouseTitle.includes('OUT_OF_STOCK'),
  );

  if (newWarehouses.length) {
    return (
      <Wrapper className="row">
        <div className="col-12">
          <label style={{ marginTop: '8px' }}>
            {t('Количество на складах')}
            <span className="help-tooltip-wrapper">
              <i className="fe-help-circle" />
              <span className="help-tooltip">
                {t('Количество на складах (включая резерв товаров)')}
              </span>
            </span>
          </label>
          <div className="stock-wrapper">
            {newWarehouses.map((w) => (
              <div className="stock-row">
                <div>{w.warehouseTitle}</div>
                <div className="stock-expired">
                  {w.stockExpired ? `${t('Просрочено')}: ${w.stockExpired}` : null}
                </div>
                <div className="stock-defective">
                  {w.stockDefective ? `${t('Брак')}: ${w.stockDefective}` : null}
                </div>
                <div className="stock-defective">
                  {w.stockDefectiveReserved
                    ? `${t('Брак (резерв)')}: ${w.stockDefectiveReserved}`
                    : null}
                </div>
                <div className="out-of-stock">
                  {w.outOfStock ? `Out of stock: ${w.outOfStock}` : null}
                </div>
                {variant && variant.id ? (
                  <Link
                    to={{
                      pathname: '/orders',
                      state: {
                        reservedVariantId: variant.id,
                        warehouseCode: w.warehouseCode,
                      },
                    }}
                    className="stock-reserved stock-reserved-hover"
                  >
                    {`${t('Резерв')}: ${w.stockReserved}`}
                  </Link>
                ) : (
                  <div className="stock-reserved">{`${t('Резерв')}: ${w.stockReserved}`}</div>
                )}
                <div
                  className={`stock-total ${
                    w.operatorType === `FULFILLMENT` ? `stock-total--disabled` : ``
                  }`}
                  onClick={() => w.operatorType !== `FULFILLMENT` && handleStockTotalModal(w)}
                >
                  {w.stockTotal}
                </div>
              </div>
            ))}
          </div>
        </div>
        {stockWithDeficit.map((stock) => {
          return (
            <div class="alert alert-warning warning mt-2">
              <i class="mdi mdi-alert-outline mr-2"></i>
              {t('На складе')} {stock.title} {t('возможна нехватка')} {stock.difference}{' '}
              {t('ЧЗ для свободных остатков')}
            </div>
          );
        })}
      </Wrapper>
    );
  }
  return null;
};

export default StockBlock;
